import ticketIcon from "@/assets/icons/ticket.svg";
import ferryIcon from "@/assets/icons/ferry.svg";
import { SectionTitle } from "@/components/common/SectionTitle";
import { ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Card, Flex, Typography, Button } from "antd";
import { useEffect, useState } from "react";
import { useAuthStore } from "@/stores/auth.store";
import { GetInventory } from "@/services/inventory.service";
import type { InventoryData } from "@/types/inventory";
import { toast } from "sonner";
import { FullScreenLoader } from "@/components/common/FullScreenLoader";

export default function InventoryPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  const [inventory, setInventory] = useState<InventoryData | null>(null);

  useEffect(() => {
    const fetchInventory = async () => {
      try {
        const agentCompanySellerId =
          useAuthStore.getState().user?.agentCompanySellerId ?? "";

        const delay = new Promise((resolve) => {
          setTimeout(resolve, 2000);
        });

        const [response] = await Promise.all([
          GetInventory({
            agentCompanySellerId,
          }),
          delay,
        ]);

        setInventory(response.data);
      } catch (error) {
        console.error("Failed to get inventory:", error);

        toast.error(
          error instanceof Error ? error.message : "Failed to get inventory.",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchInventory();
  }, []);

  const handleTopUp = () => {
    navigate("/top-up");
  };

  const handlePurchaseTicket = () => {
    navigate("/purchase-ticket");
  };

  return (
    <div className="max-w-7xl mx-auto px-4 pt-4 pb-16 sm:px-6 lg:px-8">
      <div className="flex items-center justify-between">
        <SectionTitle img={ticketIcon} title="Inventory" />
      </div>

      {/* <div className="mt-4 flex flex-col gap-4">
        <p className="text-xs text-destructive font-medium">
          You must maintain at least 10 tickets in your total inventory across
          all categories. Please top-up now to continue selling.
        </p>

        {INVENTORY_ROUTES.map((route) => (
          <section
            key={route.label}
            className="rounded-2xl bg-card p-4 shadow-xl"
          >
            <h2 className="flex items-center gap-2 text-sm font-semibold text-primary">
              <img src={ferryIcon} alt="Ferry Icon" />
              <span className="flex items-center gap-1">
                {route.origin} <ArrowRight size={14} aria-hidden="true" />
                {route.destination}
              </span>
            </h2>

            <dl className="mt-4 grid grid-cols-4 gap-4">
              {route.balances.map((balance) => (
                <div key={balance.label} className="flex flex-col gap-1">
                  <dt className="text-xs font-medium leading-tight text-field">
                    {balance.label}
                  </dt>
                  <dd className="text-2xl font-semibold text-primary">
                    {balance.count}
                  </dd>
                </div>
              ))}
            </dl>
          </section>
        ))}
        <div className="mt-4 flex flex-col gap-6 md:flex-row md:justify-end">
          <Button
            type="button"
            variant="default"
            className="w-full cursor-pointer md:w-2xs"
            onClick={handleTopUp}
          >
            Top-Up
          </Button>
          <Button
            type="button"
            variant="secondary"
            className="w-full cursor-pointer md:w-2xs"
            onClick={handlePurchaseTicket}
          >
            Purchase Ticket
          </Button>
        </div>
      </div> */}

      {loading ? (
       <FullScreenLoader />
      ) : (
        <Flex orientation="vertical" className="mt-4!">
          {inventory?.inventoryList.some(
            (item) =>
              item.adult < 10 ||
              item.child < 10 ||
              item.adultForeigner < 10 ||
              item.childForeigner < 10 ||
              item.local < 10,
          ) && (
            <Typography
              style={{ color: "var(--destructive)", fontSize: "12px" }}
              className="font-medium"
            >
              You must maintain at least 10 tickets in your total inventory
              across all categories. Please top-up now to continue selling.
            </Typography>
          )}
          {inventory?.inventoryList.map((item) => {
            const balances = [
              {
                label: "Adult (Malaysia)",
                count: item.adult,
              },
              {
                label: "Child (Malaysia)",
                count: item.child,
              },
              {
                label: "Adult (Foreigner)",
                count: item.adultForeigner,
              },
              {
                label: "Child (Foreigner)",
                count: item.childForeigner,
              },
              {
                label: "Local",
                count: item.local,
              },
            ];

            return (
              <section key={item.routeName}>
                <Card size="small" className="mt-4! shadow-lg">
                  <h2 className="flex items-center gap-2 text-sm font-semibold text-primary">
                    <img src={ferryIcon} alt="Ferry Icon" />

                    <span className="flex items-center gap-1">
                      {item.routeName.split("-").map((part, index, parts) => (
                        <span key={index} className="flex items-center gap-1">
                          {part.trim()}

                          {index < parts.length - 1 && (
                            <ArrowRight
                              size={14}
                              className="shrink-0"
                              aria-hidden="true"
                            />
                          )}
                        </span>
                      ))}
                    </span>
                  </h2>

                  <dl className="mt-4 grid grid-cols-4 gap-4 md:grid-cols-5">
                    {balances.map((balance) => (
                      <div key={balance.label} className="flex flex-col gap-1">
                        <dt className="text-xs font-medium leading-tight text-field">
                          {balance.label}
                        </dt>

                        <dd className="text-2xl font-semibold text-primary">
                          {balance.count}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </Card>
              </section>
            );
          })}

          <div className="mt-8">
            <Flex gap="large" justify="end" className="flex-col md:flex-row">
              <Button
                variant="solid"
                style={{ minWidth: "280px", borderRadius: "16px" }}
                onClick={handleTopUp}
              >
                Top-Up
              </Button>
              <Button
                style={{ minWidth: "280px", borderRadius: "16px" }}
                onClick={handlePurchaseTicket}
              >
                Purchase Ticket
              </Button>
            </Flex>
          </div>
        </Flex>
      )}
    </div>
  );
}
